import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { API_BASE, formatApiError, absolutiseApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Images, Plus, X, Trash2, Pencil, Camera, Upload, ChevronDown } from "lucide-react";

/**
 * ClientGallery — small strip of family/pets/favourite places photos for a service user.
 * Read-only for care workers; admins + managers can add/edit/delete.
 * Collapsed by default so an empty gallery doesn't consume vertical space.
 */
export default function ClientGallery({ clientId, canManage = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [viewer, setViewer] = useState(null);       // item currently open in lightbox
  const [editing, setEditing] = useState(null);     // item whose caption is being edited
  const [captionDraft, setCaptionDraft] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState(null);     // { previewUrl, file }
  const [pendingCaption, setPendingCaption] = useState("");
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.get(`/gallery/client/${clientId}`);
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const stageFile = (file) => {
    if (!file) return;
    if (!["image/jpeg","image/png","image/webp"].includes(file.type)) return toast.error("Use JPEG, PNG or WEBP.");
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo must be 5MB or less.");
    const reader = new FileReader();
    reader.onload = (e) => setPending({ previewUrl: e.target.result, file });
    reader.readAsDataURL(file);
    setPendingCaption("");
  };

  const submitPending = async () => {
    if (!pending) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pending.file);
      if (pendingCaption.trim()) fd.append("caption", pendingCaption.trim());
      await api.post(`/gallery/client/${clientId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added to gallery");
      setPending(null); setPendingCaption("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const saveCaption = async () => {
    if (!editing) return;
    try {
      await api.patch(`/gallery/${editing.id}`, { caption: captionDraft });
      toast.success("Caption updated");
      setEditing(null); setCaptionDraft("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteItem = async (item) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await api.delete(`/gallery/${item.id}`);
      toast.success("Photo removed");
      if (viewer?.id === item.id) setViewer(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const stripUrl = absolutiseApiUrl;
  const count = items.length;
  const previewThumbs = items.slice(0, 3);

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="client-gallery">
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="px-4 py-2 flex items-center gap-3">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex-1 flex items-center gap-3 text-left hover:bg-slate-50 -mx-2 px-2 py-1 rounded transition-colors"
              data-testid="gallery-toggle"
              aria-expanded={open}
            >
              <Images className="w-4 h-4 text-violet-600 shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Life &amp; conversation prompts</div>
                <h3 className="font-heading text-sm font-semibold">Photo Gallery</h3>
              </div>
              {/* Collapsed summary — only shows when the section is closed */}
              {!open && (
                <div className="flex items-center gap-2 ml-2 min-w-0" data-testid="gallery-collapsed-summary">
                  {loading ? (
                    <span className="text-[11px] text-slate-400">Loading…</span>
                  ) : count === 0 ? (
                    <span className="text-[11px] text-slate-500 italic" data-testid="gallery-summary-empty">No photos added</span>
                  ) : (
                    <>
                      <div className="flex -space-x-1.5" data-testid="gallery-summary-thumbs">
                        {previewThumbs.map((it) => (
                          <img
                            key={it.id}
                            src={stripUrl(it.photo_strip_url || it.photo_url)}
                            alt=""
                            loading="lazy"
                            className="w-6 h-6 rounded-full object-cover ring-2 ring-white border border-slate-200"
                          />
                        ))}
                      </div>
                      <span className="text-[11px] font-medium text-slate-700 tabular-nums" data-testid="gallery-summary-count">
                        {count} photo{count === 1 ? "" : "s"}
                      </span>
                    </>
                  )}
                </div>
              )}
              <ChevronDown className={`w-4 h-4 text-slate-400 ml-auto shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
          {canManage && open && (
            <div className="flex items-center gap-1.5 shrink-0">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="gallery-file-input" onChange={(e) => stageFile(e.target.files?.[0])} />
              <input ref={cameraRef} type="file" accept="image/*" capture="user" className="hidden" data-testid="gallery-camera-input" onChange={(e) => stageFile(e.target.files?.[0])} />
              <Button size="sm" variant="outline" onClick={() => cameraRef.current?.click()} className="h-8 md:hidden" data-testid="gallery-take">
                <Camera className="w-3.5 h-3.5 mr-1.5" /> Take
              </Button>
              <Button size="sm" onClick={() => fileRef.current?.click()} className="h-8 bg-violet-700 hover:bg-violet-800" data-testid="gallery-add">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Photo
              </Button>
            </div>
          )}
        </div>

        <CollapsibleContent>
          <div className="border-t border-slate-100">
            {loading ? (
              <div className="p-4 text-sm text-slate-500" data-testid="gallery-loading">Loading photos…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="gallery-empty">
                <Images className="w-6 h-6 mx-auto text-slate-300 mb-2" />
                No photos yet. Add pictures of family, pets or favourite places to help carers connect.
              </div>
            ) : (
              <div className="p-3 overflow-x-auto">
                <ul className="flex gap-3" data-testid="gallery-strip">
                  {items.map((it) => (
                    <li key={it.id} className="shrink-0 w-40 group relative" data-testid={`gallery-item-${it.id}`}>
                      <button
                        type="button"
                        onClick={() => setViewer(it)}
                        className="w-40 h-40 rounded-md overflow-hidden bg-slate-100 border border-slate-200 hover:border-violet-400 hover:ring-2 hover:ring-violet-200 transition"
                        data-testid={`gallery-open-${it.id}`}
                        title={it.caption || "View"}
                      >
                        <img src={stripUrl(it.photo_strip_url || it.photo_url)} alt={it.caption || "Gallery photo"} className="w-full h-full object-cover" loading="lazy" />
                      </button>
                      {canManage && (
                        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditing(it); setCaptionDraft(it.caption || ""); }}
                            className="p-1 bg-white/95 border border-slate-200 rounded shadow-sm hover:text-sky-700"
                            data-testid={`gallery-edit-${it.id}`}
                            title="Edit caption"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteItem(it); }}
                            className="p-1 bg-white/95 border border-slate-200 rounded shadow-sm text-red-600 hover:text-red-700"
                            data-testid={`gallery-delete-${it.id}`}
                            title="Remove"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <div className="mt-1.5 text-[11px] text-slate-700 truncate" title={it.caption || ""}>
                        {it.caption || <span className="text-slate-400 italic">Untitled</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Upload confirmation dialog with caption input */}
      <Dialog open={!!pending} onOpenChange={(v) => { if (!v && !uploading) { setPending(null); setPendingCaption(""); } }}>
        <DialogContent className="max-w-md bg-white" data-testid="gallery-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Upload className="w-4 h-4" /> Add gallery photo</DialogTitle>
            <DialogDescription>Give this picture a short caption so carers know its story.</DialogDescription>
          </DialogHeader>
          {pending?.previewUrl && (
            <div className="w-full max-h-72 overflow-hidden rounded bg-slate-100 border border-slate-200 flex items-center justify-center">
              <img src={pending.previewUrl} alt="Preview" className="max-w-full max-h-72 object-contain" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Caption</label>
            <Input value={pendingCaption} onChange={(e) => setPendingCaption(e.target.value)} placeholder="e.g. Sarah with her granddaughter Mia" data-testid="gallery-caption-input" className="mt-1" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPending(null); setPendingCaption(""); }} disabled={uploading} data-testid="gallery-upload-cancel">Cancel</Button>
            <Button onClick={submitPending} disabled={uploading} className="bg-violet-700 hover:bg-violet-800" data-testid="gallery-upload-submit">
              {uploading ? "Uploading…" : "Add to Gallery"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Caption edit */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setCaptionDraft(""); } }}>
        <DialogContent className="max-w-md bg-white" data-testid="gallery-caption-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit caption</DialogTitle>
          </DialogHeader>
          <Input value={captionDraft} onChange={(e) => setCaptionDraft(e.target.value)} placeholder="Caption" data-testid="gallery-caption-edit-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="gallery-caption-cancel">Cancel</Button>
            <Button onClick={saveCaption} className="bg-sky-700 hover:bg-sky-800" data-testid="gallery-caption-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox viewer */}
      <Dialog open={!!viewer} onOpenChange={(v) => { if (!v) setViewer(null); }}>
        <DialogContent className="max-w-3xl bg-white" data-testid="gallery-viewer">
          <DialogHeader>
            <DialogTitle className="font-heading text-base flex items-center justify-between gap-3">
              <span className="truncate">{viewer?.caption || "Gallery photo"}</span>
              <button onClick={() => setViewer(null)} className="p-1 text-slate-500 hover:text-slate-900" data-testid="gallery-viewer-close">
                <X className="w-4 h-4" />
              </button>
            </DialogTitle>
            {viewer?.added_by_name && (
              <div className="text-[11px] text-slate-500">Added by {viewer.added_by_name}{viewer.added_at ? ` · ${new Date(viewer.added_at).toLocaleDateString()}` : ""}</div>
            )}
          </DialogHeader>
          {viewer && (
            <div className="w-full flex items-center justify-center bg-slate-950 rounded overflow-hidden">
              <img src={stripUrl(viewer.photo_url)} alt={viewer.caption || "Gallery photo"} className="max-h-[70vh] max-w-full object-contain" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
